import { Alert } from '@mui/material';
import React from 'react';

import { ClientLaunchType } from '../../../graphql/types';
import { BasicsStep } from './BasicsStep';
import { EligibilityStep } from './EligibilityStep';
import {
  ELIGIBILITY_STEP_ID,
  REVIEW_STEP_ID,
  SIGN_IN_STEP_ID,
} from './launchState';
import { ReviewStep } from './ReviewStep';
import { SignInStep } from './SignInStep';
import type { CheckpointItem, LaunchStep, SetupChoices } from './types';

// Studios, features and coaches are placeholders: each flow ticket replaces
// one with its real form. Checkpoint items come from the mockup, and their
// wording is still waiting on stakeholder feedback.

const PlaceholderStep: React.FC = () => (
  <Alert severity="info">This step's fields aren't built yet.</Alert>
);

const noCheckpoint = () => [];

const studiosCheckpoint = (): CheckpointItem[] => [
  {
    id: 'site',
    label: 'Each MBO site ID was checked against the site in Mindbody',
    help: 'There is no Mindbody sandbox. A wrong site ID points members at a real, unrelated studio.',
  },
  {
    id: 'api',
    label: 'Each studio has been authenticated to be used with the MBO API',
  },
  {
    id: 'tz',
    label: 'Studio time zones match the physical gyms',
    help: 'Class times in the app are shown in the studio time zone.',
  },
];

const eligibilityCheckpoint = (
  _launch: unknown,
  choices: SetupChoices,
): CheckpointItem[] => [
  ...(choices.eligFile
    ? [
        {
          id: 'sftp',
          label: 'Client has an SFTP account and has the file format',
          help: 'A CSV with email addresses in the first column. Optional columns, any capitalization: Office ID (or OfficeID, Work Location ID, WorkLocationID) and Employee Type (or EmployeeType, employment_type).',
        },
      ]
    : []),
  ...(choices.eligGym
    ? [
        {
          id: 'gym',
          label:
            'Client agreed that anyone with an active gym membership gets app access',
        },
      ]
    : []),
  ...(choices.eligDomainOnly
    ? [
        {
          id: 'dom',
          label: 'Each email domain is owned by this client',
          help: 'A wrong domain lets outsiders sign up as this client.',
        },
        {
          id: 'open',
          label:
            'Client accepted that anyone with a work email at these domains gets access',
          help: 'Former employees keep access for as long as their email works.',
        },
      ]
    : []),
  ...(choices.eligFile && choices.fileHasOffices
    ? [
        {
          id: 'cols',
          label:
            'Every office ID in the file is listed above, spelled exactly the same',
          help: "Matching is exact, including capitals. Check this before the client's first upload.",
        },
      ]
    : []),
];

const eligibilitySummary = (_launch: unknown, choices: SetupChoices) =>
  [
    choices.eligFile && 'Eligibility file',
    choices.eligGym && 'Gym membership',
    choices.eligDomainOnly && 'Email domain',
  ]
    .filter(Boolean)
    .join(' + ') || 'None';

const signInCheckpoint = (
  _launch: unknown,
  choices: SetupChoices,
): CheckpointItem[] => {
  const registration: CheckpointItem = {
    id: 'reg',
    label: 'An eligible user was able to complete registration for the app',
    help: choices.usesSSO
      ? "Checks sign-in and eligibility together. Use a test user from the Eligibility step and sign in through the client's identity provider."
      : 'Checks sign-in and eligibility together. Use a test user from the Eligibility step.',
  };
  if (!choices.usesSSO) {
    return [registration];
  }
  return [
    {
      id: 'sso',
      label: "Engineering has configured the client's Auth0 connection",
    },
    registration,
    ...(choices.ssoConnection
      ? [
          {
            id: 'conn',
            label: 'Connection name matches Auth0 exactly',
            help: 'Also used for the FCM roster upload path fcm/{connection}/eligible.csv.',
          },
        ]
      : []),
  ];
};

const featuresCheckpoint = (): CheckpointItem[] => [
  {
    id: 'feat',
    label:
      'I have reviewed this selection of features and confirm it is correct',
  },
];

const featureNames: LaunchStep['summary'] = (launch) =>
  launch.features.map((f) => f.name).join(', ') || 'None';

const basics: LaunchStep = {
  id: 'basics',
  title: 'Client basics',
  subtitle: 'Name and logo',
  intro: 'Continuing creates the client. Each later step saves as you go.',
  fieldNames: ['clients'],
  checkpoint: noCheckpoint,
  summary: (launch) => launch.client.name ?? '',
  Component: BasicsStep,
};

const eligibility: LaunchStep = {
  id: ELIGIBILITY_STEP_ID,
  title: 'Eligibility',
  subtitle: 'Who gets access',
  intro: 'Who can sign up as a member of this client.',
  fieldNames: ['clients', 'eligible_email_addresses', 'domains', 'offices'],
  checkpoint: eligibilityCheckpoint,
  summary: eligibilitySummary,
  choiceKeys: ['eligFile', 'eligDomainOnly', 'fileHasOffices'],
  Component: EligibilityStep,
};

const signIn: LaunchStep = {
  id: SIGN_IN_STEP_ID,
  title: 'Sign-in method',
  subtitle: 'SSO or password',
  fieldNames: ['clients'],
  checkpoint: signInCheckpoint,
  summary: (_launch, choices) =>
    choices.usesSSO ? 'Single sign-on' : 'Email & password',
  choiceKeys: ['usesSSO'],
  Component: SignInStep,
};

const studios: LaunchStep = {
  id: 'studios',
  title: 'Studios & locations',
  subtitle: 'Gyms in Mindbody',
  intro:
    'Each studio is one Mindbody site. Members join a gym at one of its locations.',
  fieldNames: ['studios', 'locations', 'mbo_contract_mappings'],
  checkpoint: studiosCheckpoint,
  Component: PlaceholderStep,
};

const features: LaunchStep = {
  id: 'features',
  title: 'Features',
  subtitle: 'What members get',
  fieldNames: ['clients', 'feature_grants'],
  checkpoint: featuresCheckpoint,
  summary: featureNames,
  Component: PlaceholderStep,
};

const coaches: LaunchStep = {
  id: 'coaches',
  title: 'Coaches',
  subtitle: 'Who serves members',
  fieldNames: ['coach_client'],
  checkpoint: noCheckpoint,
  Component: PlaceholderStep,
};

const review: LaunchStep = {
  id: REVIEW_STEP_ID,
  title: 'Review & finish',
  subtitle: 'Final sign-off',
  intro: 'Everything below is saved.',
  checkpoint: () => [
    {
      id: 'diag',
      label: 'Client diagnostics ran with no issues',
      help: 'Ticks itself when the latest run is clean. Run it again after any change.',
      isAutoDone: ({ diagnosticsClean }) => diagnosticsClean,
    },
    { id: 'second', label: 'A second person reviewed this setup' },
    {
      id: 'launch',
      label:
        'Client contact knows the launch date and member communications are ready',
    },
  ],
  Component: ReviewStep,
};

const NEW_CLIENT_STEPS = [
  basics,
  eligibility,
  signIn,
  studios,
  features,
  coaches,
  review,
];

const FEATURE_LAUNCH_STEPS: LaunchStep[] = [
  {
    ...studios,
    title: 'Review studio configuration',
    subtitle: 'Mindbody readiness',
    intro: 'Features need every studio set up and authenticated in Mindbody.',
  },
  {
    ...features,
    subtitle: 'Test users, then everyone',
    fieldNames: ['feature_grants', 'eligible_email_addresses'],
  },
  review,
];

export const getLaunchSteps = (type: ClientLaunchType) =>
  type === ClientLaunchType.FeatureLaunch
    ? FEATURE_LAUNCH_STEPS
    : NEW_CLIENT_STEPS;
